import React, {
    memo,
    useState,
    useEffect,
    useRef,
    useLayoutEffect,
} from "react";
import { IChannel } from "@Interfaces/Entities/Channels";
import {
    Gradient,
    ChannelCover,
    StyledChannelHeaderWrapper,
    StyledChannelHeaderCover,
    StyledChannelHeaderInfo,
    StyledBadgeAvatar,
    StyledAvatarContainer,
    StyledInfoChannel,
    StyledHeader,
    StyledDescription,
    StyledAvatar,
    StyledChannelHeaderDescription,
    StyledPresentModeAction,
} from "./ChannelHeader.styles";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import { useTranslation } from "react-i18next";
import CreateIcon from "@mui/icons-material/Create";
import { PRESENT_MODE_DIALOG_WIDTH } from "@Components/Dialog/PresentModeDialog";
import { ChannelMenu } from "@Components/ChannelMenu";
import { UploadCoverWrapper } from "./ChannelHeader.styles";
import { useSelector } from "react-redux";
import { RootStateType } from "@Interfaces";
import { useDispatch } from "react-redux";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import { ImageType } from "@Interfaces/Entities/Images";
import Badge from "@mui/material/Badge";
import UrlifyLink from "@Components/UrlLink";
import {
    CHANNEL_DEFAULT_IMAGE_DATA_URI,
    EDIT_SMALL_AVATAR_IMAGE_DATA_URI,
} from "../../Utils/assets";
import { getChannelCoverUrl } from "@Utils/getChannelCoverUrl";
import {
    getClientPresentEligibility,
    getIneligibleReasonKey,
} from "@Utils/presentation";
import { isUserChannelAdmin } from "@Utils/isUserChannelAdmin";

type ChannelHeaderProps = IChannel & {
    /** Shows the admin's "Present Mode" button (event page only, not the participant page). */
    showPresentMode?: boolean;
};

const MemoizedChannelHeader: React.FunctionComponent<ChannelHeaderProps> = ({
    showPresentMode = false,
    ...channel
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const shouldDisplayProfilePicture =
        channel.settings?.display_profile_picture ?? true;
    const userId = useSelector((state: RootStateType) => state.auth.user.id);
    const isAdmin = isUserChannelAdmin(channel, userId);
    const hue = channel.cover?.hue ? channel.cover?.hue : 22;

    const getRandomHue = (hue: number) => {
        return Math.floor(hue * (1 - 360)) + 1;
    };

    const handleCoverUploadDialogOpen = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.UploadChannelCoverDialog,
                props: { channel, imageType: ImageType.ChannelCover },
            })
        );
    };

    const presentEligibility = getClientPresentEligibility(channel);

    const handlePresentModeOpen = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.PresentModeDialog,
                props: {
                    channel,
                    paperWidth: PRESENT_MODE_DIALOG_WIDTH,
                },
            })
        );
    };

    const handleProfileImgUploadDialogOpen = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.UploadChannelCoverDialog,
                props: { channel, imageType: ImageType.ChannelProfile },
            })
        );
    };

    //#### Atomic-Components #####
    const Cover = () => {
        const coverUrl = getChannelCoverUrl(channel);

        return coverUrl ? (
            <ChannelCover url={coverUrl}>
                <HeaderMenu {...channel} />
            </ChannelCover>
        ) : (
            <Gradient hue={getRandomHue(hue)}>
                <HeaderMenu {...channel} />
            </Gradient>
        );
    };

    //@todo we could merge this with Hook useChannelProfileImg.tsx
    const ImgSrc = channel.profile_image
        ? `${channel?.profile_image?.public_url}/${channel?.profile_image?.fid}`
        : CHANNEL_DEFAULT_IMAGE_DATA_URI;

    const HeaderMenu = (channel: IChannel) => (
        <Box
            sx={{ display: { xs: "block", md: "none" } }}
            style={{ position: "absolute", top: 15, right: 10 }}
        >
            <ChannelMenu channel={channel} renderInList={false} />
        </Box>
    );

    const UploadCoverMenu = () => (
        <UploadCoverWrapper>
            <Button
                aria-controls="simple-menu"
                aria-haspopup="true"
                onClick={handleCoverUploadDialogOpen}
            >
                <CreateIcon style={{ color: "white" }} />
            </Button>
        </UploadCoverWrapper>
    );

    const ChannelDescription = ({ desc }: { desc: string }) => (
        <StyledChannelHeaderDescription>
            <UrlifyLink text={desc} />
        </StyledChannelHeaderDescription>
    );

    const [isSticky, setIsSticky] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const getStickyOffset = () => {
            const rootFontSize = parseFloat(
                getComputedStyle(document.documentElement).fontSize || "16"
            );
            const multiplier = window.matchMedia("(max-width: 500px)").matches
                ? 5.4
                : 5;

            if (Number.isNaN(rootFontSize)) {
                return 0;
            }

            return rootFontSize * multiplier;
        };

        const handleScrollEvent = () => {
            if (!wrapperRef.current || !headerRef.current) return;

            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            const wrapperTopOffset = wrapperRect.top + window.scrollY;
            const headerOffset = headerRef.current.offsetTop;
            const shouldStick =
                window.scrollY + getStickyOffset() >=
                wrapperTopOffset + headerOffset;

            setIsSticky((prev) => (prev === shouldStick ? prev : shouldStick));
        };

        handleScrollEvent();
        window.addEventListener("scroll", handleScrollEvent, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScrollEvent);
        };
    }, []);

    const [widthElement, setWidthElement] = useState(0);
    useLayoutEffect(() => {
        const updateWidth = () => {
            setWidthElement(wrapperRef?.current?.clientWidth ?? 0);
        };

        updateWidth();
        window.addEventListener("resize", updateWidth);

        return () => {
            window.removeEventListener("resize", updateWidth);
        };
    }, []);

    return (
        <>
            <StyledChannelHeaderWrapper
                id={`channel-${channel._id}`}
                className="tb-shadow-box"
                ref={wrapperRef}
            >
                <StyledChannelHeaderCover>
                    <Cover />
                    {isAdmin && !channel.closed ? <UploadCoverMenu /> : null}
                </StyledChannelHeaderCover>
                <StyledChannelHeaderInfo
                    ref={headerRef}
                    isSticky={isSticky}
                    widthElement={widthElement}
                    hasAvatar={shouldDisplayProfilePicture}
                >
                    {shouldDisplayProfilePicture && (
                        <StyledAvatarContainer
                            isSticky={isSticky}
                            widthElement={widthElement}
                        >
                            <Badge
                                overlap="circular"
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                }}
                                badgeContent={
                                    isAdmin &&
                                    !channel.closed && (
                                        <StyledBadgeAvatar
                                            alt="profile image"
                                            src={
                                                EDIT_SMALL_AVATAR_IMAGE_DATA_URI
                                            }
                                            style={{
                                                width: "30px",
                                                height: "30px",
                                            }}
                                            onClick={
                                                handleProfileImgUploadDialogOpen
                                            }
                                        />
                                    )
                                }
                            >
                                <StyledAvatar
                                    src={ImgSrc}
                                    alt={channel.description}
                                    style={{
                                        width: isSticky ? "4.5rem" : "7.5rem",
                                        height: isSticky ? "4.5rem" : "7.5rem",
                                    }}
                                />
                            </Badge>
                        </StyledAvatarContainer>
                    )}
                    {showPresentMode && isAdmin && (
                        <StyledPresentModeAction
                            sx={{ display: { xs: "none", md: "block" } }}
                        >
                            <Tooltip
                                title={
                                    presentEligibility.reason
                                        ? t(
                                              getIneligibleReasonKey(
                                                  presentEligibility.reason
                                              )
                                          )
                                        : ""
                                }
                            >
                                {/* The span keeps the tooltip working on a disabled button. */}
                                <span>
                                    <Button
                                        variant="outlined"
                                        disableFocusRipple
                                        startIcon={
                                            <DesktopWindowsOutlinedIcon />
                                        }
                                        onClick={handlePresentModeOpen}
                                        disabled={!presentEligibility.enabled}
                                        sx={{
                                            minHeight: 44,
                                            px: "18px",
                                            gap: "2px",
                                            // Inter, as the mockup and the rest of Present Mode.
                                            fontFamily:
                                                '"Inter", "Roboto", system-ui, sans-serif',
                                            borderRadius: "8px",
                                            border: "1px solid #C9CEF0",
                                            textTransform: "none",
                                            // Sized against the app's ~26px title, as
                                            // the mockup sizes it against its 38px one.
                                            fontSize: 14,
                                            fontWeight: 500,
                                            "& .MuiButton-startIcon svg": {
                                                fontSize: 18,
                                            },
                                            color: "#4F5BD5",
                                            backgroundColor: "#FFFFFF",
                                            "&:hover": {
                                                borderColor: "#3B45B0",
                                                color: "#3B45B0",
                                                backgroundColor: "#E6E8FA",
                                            },
                                            "&:active": {
                                                backgroundColor: "#E6E8FA",
                                            },
                                            "&:focus-visible": {
                                                outline: "3px solid #4F5BD5",
                                                outlineOffset: "2px",
                                            },
                                            "&.Mui-disabled": {
                                                color: "#8A93A6",
                                                borderColor: "#E1E4EC",
                                            },
                                        }}
                                    >
                                        {t("present.entry.button")}
                                    </Button>
                                </span>
                            </Tooltip>
                        </StyledPresentModeAction>
                    )}
                    <StyledInfoChannel
                        hasAvatar={shouldDisplayProfilePicture}
                        $reserveAction={showPresentMode && isAdmin}
                    >
                        <StyledHeader>{channel.title}</StyledHeader>
                        <StyledDescription isSticky={isSticky}>
                            <ChannelDescription desc={channel.description} />
                        </StyledDescription>
                    </StyledInfoChannel>
                </StyledChannelHeaderInfo>
            </StyledChannelHeaderWrapper>
        </>
    );
};

export const ChannelHeader = memo(MemoizedChannelHeader);
